import { useHandler } from "@redotech/react-util/hook";
import FilterIcon from "@redotech/redo-icons/filter-lines.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import {
  PERFORMANCE_DIMENSION_LABELS,
  PERFORMANCE_DIMENSIONS,
  PERFORMANCE_VALUE_LABELS,
  sortDimensions,
  type PerformanceDimension,
  type PerformanceFilters,
} from "@redotech/redo-marketing-common/performance-report";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoFilterDropdownAnchor,
  RedoFilterGroup,
  RedoFilterSection,
} from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { SimpleMultiSelectFilterDropdown } from "@redotech/redo-web/filters/simple-multiselect-filter-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useState } from "react";

const DIMENSION_ITEMS: RedoListItem<PerformanceDimension>[] =
  PERFORMANCE_DIMENSIONS.map((dimension) => ({
    id: dimension,
    type: "text",
    text: PERFORMANCE_DIMENSION_LABELS[dimension],
    value: dimension,
  }));

/** Keeps the current order and appends what was just checked, so filter
 *  chips stay where they were added. */
function inCheckedOrder<T>(current: readonly T[], picked: readonly T[]): T[] {
  return [
    ...current.filter((value) => picked.includes(value)),
    ...picked.filter((value) => !current.includes(value)),
  ];
}

export const AttributionFilters = memo(function AttributionFilters({
  groupBy,
  setGroupBy,
  filters,
  setFilters,
}: {
  groupBy: readonly PerformanceDimension[];
  setGroupBy(groupBy: PerformanceDimension[]): void;
  filters: PerformanceFilters;
  setFilters(filters: PerformanceFilters): void;
}) {
  const [shown, setShown] = useState<PerformanceDimension[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupAnchor, setGroupAnchor] = useState<HTMLDivElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);

  const pickFilters = useHandler(
    (items: RedoListItem<PerformanceDimension>[]) => {
      const next = inCheckedOrder(
        shown,
        items.map((item) => item.value),
      );
      setShown(next);
      setFilters(
        Object.fromEntries(
          next.flatMap((dimension) => {
            const values = filters[dimension];
            return values ? [[dimension, values]] : [];
          }),
        ),
      );
    },
  );

  return (
    <Flex align="center" gap="sm" pt="md" wrap="wrap">
      <RedoFilterGroup
        Icon={LayersIcon}
        propertyName="Group by"
        query={<RedoFilterSection text="is" />}
        value={
          <Flex gap="none" ref={setGroupAnchor}>
            {groupBy.map((dimension, index) => (
              <RedoFilterDropdownAnchor
                key={dimension}
                onClick={() => setGroupOpen(!groupOpen)}
                text={`${PERFORMANCE_DIMENSION_LABELS[dimension]}${index < groupBy.length - 1 ? "," : ""}`}
                weight="medium"
              />
            ))}
          </Flex>
        }
      />
      <RedoMultiselectDropdown
        dropdownAnchor={groupAnchor}
        dropdownOpen={groupOpen}
        items={DIMENSION_ITEMS}
        selectedItems={DIMENSION_ITEMS.filter((item) =>
          groupBy.includes(item.value),
        )}
        setDropdownOpen={setGroupOpen}
        setSelectedItems={(items) => {
          if (items.length > 0) {
            setGroupBy(sortDimensions(items.map((item) => item.value)));
          }
        }}
      />
      {shown.map((dimension) => {
        const labels = PERFORMANCE_VALUE_LABELS[dimension];
        return (
          <RedoFilterGroup
            Icon={FilterIcon}
            key={dimension}
            propertyName={PERFORMANCE_DIMENSION_LABELS[dimension]}
            query={<RedoFilterSection text="is" />}
            removeFilter={() =>
              pickFilters(
                DIMENSION_ITEMS.filter(
                  (item) =>
                    item.value !== dimension && shown.includes(item.value),
                ),
              )
            }
            value={
              <SimpleMultiSelectFilterDropdown<string>
                filterStyle="value"
                options={Object.keys(labels)}
                optionToFriendlyName={(value) => labels[value] ?? value}
                selectedOptionsToDisplay={(selected) =>
                  selected && selected.length > 0
                    ? selected.map((value) => labels[value] ?? value).join(", ")
                    : "..."
                }
                setValues={(values) =>
                  setFilters({ ...filters, [dimension]: values })
                }
                values={[...(filters[dimension] ?? [])]}
              />
            }
          />
        );
      })}
      <RedoButton
        aria-label="Filter"
        hierarchy="secondary"
        IconLeading={FilterIcon}
        onClick={() => setMenuOpen(!menuOpen)}
        ref={setMenuAnchor}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={menuAnchor}
        dropdownOpen={menuOpen}
        items={DIMENSION_ITEMS}
        selectedItems={DIMENSION_ITEMS.filter((item) =>
          shown.includes(item.value),
        )}
        setDropdownOpen={setMenuOpen}
        setSelectedItems={pickFilters}
      />
      {(groupBy.length !== PERFORMANCE_DIMENSIONS.length ||
        shown.length > 0) && (
        <RedoButton
          hierarchy="tertiary"
          onClick={() => {
            setGroupBy(PERFORMANCE_DIMENSIONS);
            setShown([]);
            setFilters({});
          }}
          size="xs"
          text="Reset to default"
        />
      )}
    </Flex>
  );
});
